import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatLegacyButton as MatButton } from '@angular/material/legacy-button';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { guid } from 'app/shared/Utils/common.utils';
import { CustomObjectValueTemplateTab } from '../../../custom-objects.types';
import { FormPanelMeta, PanelDef } from 'app/shared/components/panels/panels.types';

@Component({
  selector: 'app-custom-objects-details-tab-nav',
  templateUrl: './tab-nav.component.html',
  styleUrls: ['./tab-nav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomObjectsDetailsTabNavComponent extends OnDestroyMixin {
  @Input() tabs: CustomObjectValueTemplateTab[];
  @Input() tabId: string;
  @Input() editMode = false;
  @Input() canEditLayout = true;
  @Output() panelAdded = new EventEmitter<PanelDef<any>>();
  @Output() tabAdded = new EventEmitter<CustomObjectValueTemplateTab>();
  @Output() tabUpdated = new EventEmitter<CustomObjectValueTemplateTab>();
  @Output() tabRemoved = new EventEmitter<CustomObjectValueTemplateTab>();
  @Output() tabMoved = new EventEmitter<CdkDragDrop<any[]>>();
  @Output() toggleEditMode = new EventEmitter();
  @Output() layoutSaved = new EventEmitter<boolean>();
  @Output() tabChange = new EventEmitter<string>();

  tabForm = new FormGroup({
    name: new FormControl('', [Validators.required]),
    id: new FormControl(null),
  });

  panels: PanelDef<FormPanelMeta>[] = [
    { id: guid(), type: 'activity', title: 'Activity', meta: { fields: [] } },
    { id: guid(), type: 'form', title: 'Form', meta: { fields: [] } },
    { id: guid(), type: 'comments', title: 'Comments', meta: { fields: [] } },
    { id: guid(), type: 'table', title: 'Related Objects Table', meta: { fields: [] } },
    { id: guid(), type: 'matrix', title: 'Matrix', meta: { fields: [] } },
    { id: guid(), type: 'images', title: 'Gallery', meta: { fields: [] } },
  ];

  @ViewChild('tabPanel') private tabPanel: TemplateRef<any>;
  @ViewChild('tabPanelOrigin') private tabPanelOrigin: MatButton;

  private tabPanelOverlayRef: OverlayRef;
  private tabPanelTemplatePortal: TemplatePortal;

  constructor(
    private readonly overlay: Overlay,
    private readonly viewContainerRef: ViewContainerRef,
  ) {
    super();
  }

  drop(event: CdkDragDrop<any[]>): void {
    this.tabMoved.emit(event);
  }

  openTabPanel(tab?: CustomObjectValueTemplateTab): void {
    if (tab) {
      this.tabForm.setValue({ id: tab.id, name: tab.name });
    }

    this.tabPanelOverlayRef = this.overlay.create({
      backdropClass: '',
      hasBackdrop: true,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.tabPanelOrigin._elementRef.nativeElement)
        .withPositions([{ originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' }]),
    });

    this.tabPanelOverlayRef
      .attachments()
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => this.tabPanelOverlayRef.overlayElement.querySelector('input').focus());

    this.tabPanelTemplatePortal = new TemplatePortal(this.tabPanel, this.viewContainerRef);

    this.tabPanelOverlayRef.attach(this.tabPanelTemplatePortal);

    this.tabPanelOverlayRef
      .backdropClick()
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => this.closeTabPanel());
  }

  saveTab(): void {
    const tab = this.tabForm.getRawValue();
    (tab.id ? this.tabUpdated : this.tabAdded).emit(tab);
    this.closeTabPanel();
  }

  saveLayout(cancel = false): void {
    this.layoutSaved.emit(cancel);
  }

  removeTab(tab: CustomObjectValueTemplateTab): void {
    this.tabRemoved.emit(tab);
  }

  private closeTabPanel(): void {
    if (this.tabPanelOverlayRef?.hasAttached()) this.tabPanelOverlayRef.detach();
    if (this.tabPanelTemplatePortal?.isAttached) this.tabPanelTemplatePortal.detach();

    this.tabForm.setValue({ id: null, name: '' });
  }
}
